import React, { useState, useEffect } from "react";
import "../App.css";
import calcImg from "../assets/indication_formula.png";
import FormulaDisplay from "./FormulaDisplay";

const kinds = [
  { label: "二氧化硫", value: "so2" },
  { label: "氮氧化物", value: "nox" },
  { label: "氧气", value: "o2" },
  { label: "颗粒物", value: "pm" },
  { label: "非甲烷总烃", value: "nmhc" },
];

const ranges = [
  { label: "0-25", value: "0-25" },
  { label: "0-30", value: "0-30" },
  { label: "0-100", value: "0-100" },
  { label: "0-120", value: "0-120" },
  { label: "自定义", value: "custom" },
];

const getDefaultRange = (kind) => {
  if (kind === "o2") return "0-25";
  if (kind === "pm") return "0-30";
  if (kind === "nmhc") return "0-120";
  return "0-100";
};

const getRangeInfo = (rangeStr) => {
  if (!rangeStr || !rangeStr.includes("-")) return { min: 0, max: 0, diff: 0 };
  const [min, max] = rangeStr.split("-").map(Number);
  return { min, max, diff: max - min };
};

const getKindLabel = (kind) => {
  const found = kinds.find(k => k.value === kind);
  return found ? found.label : kind;
};

const getErrorLimit = (kind, max) => {
  if (["so2", "nox", "nmhc"].includes(kind)) {
    if ((kind === "so2" && max > 286) || (kind === "nox" && max > 410) || (kind === "nmhc" && max > 100)) {
      return 5;
    }
    return 2.5;
  }
  return 2.5;
};

const getErrorFormulaType = (kind, max) => {
  // 氧气始终使用参考值作为分母
  if (kind === "o2") {
    return "ref"; // 用参考值做分母
  }
  
  if (["so2", "nox", "nmhc"].includes(kind)) {
    if ((kind === "so2" && max > 286) || (kind === "nox" && max > 410) || (kind === "nmhc" && max > 100)) {
      return "ref"; // 用参考值做分母
    }
    return "range"; // 用量程做分母
  }
  return "range";
};

const getResponseLimit = (kind) => {
  if (["so2", "nox", "o2"].includes(kind)) return 200;
  if (kind === "nmhc") return 300;
  return 200;
};

const IndicationError = () => {
  const [kind, setKind] = useState("so2");
  const [range, setRange] = useState(getDefaultRange("so2"));
  const [customRange, setCustomRange] = useState("");
  const [rangeInfo, setRangeInfo] = useState(getRangeInfo(getDefaultRange("so2")));

  // 只保留高标
  const [ref, setRef] = useState("");
  const [values, setValues] = useState(["", "", ""]);
  const [t1, setT1] = useState(["", "", ""]);
  const [t2, setT2] = useState(["", "", ""]);

  // 监听种类变化，自动切换量程
  useEffect(() => {
    const defaultRange = getDefaultRange(kind);
    setRange(defaultRange);
    setCustomRange("");
    const info = getRangeInfo(defaultRange);
    setRangeInfo(info);
    setRef(info.max ? (info.max * 1).toFixed(2) : "");
    setValues(["", "", ""]);
    setT1(["", "", ""]);
    setT2(["", "", ""]);
  }, [kind]);

  // 计算
  const nums = values.map(v => v === "" ? 0 : Number(v));
  const avg = (nums.reduce((a, b) => a + b, 0) / 3).toFixed(2);
  const refNum = ref === "" ? 0 : Number(ref);
  const limit = getErrorLimit(kind, rangeInfo.max);
  const label = getKindLabel(kind);
  const formulaType = getErrorFormulaType(kind, rangeInfo.max);
  let error = "";
  let errorPass = "";
  let errorPassDetail = "";
  if (values.some(v => v !== "")) {
    if (formulaType === "range" && rangeInfo.diff) {
      error = (((avg - refNum) / rangeInfo.diff) * 100).toFixed(2);
    } else if (formulaType === "ref" && refNum) {
      error = (((avg - refNum) / refNum) * 100).toFixed(2);
    }
    if (error !== "") {
      errorPass = Math.abs(error) <= limit ? "合格" : "不合格";
      errorPassDetail = `（${label}限值±${limit}%）`;
    }
  }
  // 响应时间
  const t1Arr = t1.map(v => v === "" ? 0 : Number(v));
  const t2Arr = t2.map(v => v === "" ? 0 : Number(v));
  const tSumArr = t1Arr.map((t1v, i) => t1v + t2Arr[i]);
  const tAvg = (tSumArr.reduce((a, b) => a + b, 0) / 3).toFixed(2);
  const tLimit = getResponseLimit(kind);
  let tPass = "";
  if (t1.some(v => v !== "") || t2.some(v => v !== "")) {
    tPass = `${tAvg <= tLimit ? "合格" : "不合格"}`;
  }

  // 公式与过程
  let formulaStr = "";
  if (formulaType === "range") {
    formulaStr = `(${avg} - ${refNum}) / ${rangeInfo.diff} × 100 = ${error}`;
  } else {
    formulaStr = `(${avg} - ${refNum}) / ${refNum} × 100 = ${error}`;
  }

  // 图片放大
  const [showImg, setShowImg] = useState(false);

  return (
    <div style={{ padding: 24, maxWidth: 600, margin: "0 auto" }}>
      <div style={{ display: "flex", gap: 24, marginBottom: 16, flexWrap: 'wrap' }}>
        <div>
          <label>种类：</label>
          <select value={kind} onChange={e => setKind(e.target.value)}>
            {kinds.map(k => (
              <option key={k.value} value={k.value}>{k.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label>分析仪量程：</label>
          <select value={range} onChange={e => setRange(e.target.value)}>
            {ranges.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
          {range === "custom" && (
            <input
              type="text"
              placeholder="如 0-150"
              value={customRange}
              onChange={e => setCustomRange(e.target.value)}
              style={{ width: 90 }}
            />
          )}
        </div>
        <div>
          <label>计量单位：</label>
          <span>mg/m³</span>
        </div>
      </div>
      <h3>示值误差检查</h3>
      <div style={{ background: '#e3f2fd', borderRadius: 24, boxShadow: '0 2px 8px #e5e9f2', padding: 24, display: 'flex', flexDirection: 'column', gap: 16, minWidth: 320, maxWidth: 480, margin: '0 auto' }}>
        {/* 第一行：参考值 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ fontSize: 13, marginRight: 6 }}>标准气体或校准器件参考值C<sub>st</sub></div>
          <input type="number" value={ref} onChange={e => setRef(e.target.value)} style={{ width: 90, borderRadius: 10, padding: 6 }} />
        </div>
        {/* 第二行：显示值、平均值/误差、合格性 */}
        <div style={{ display: 'flex', gap: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 70 }}>
            <div style={{ fontSize: 13, marginBottom: 2 }}>显示值</div>
            {values.map((v, j) => (
              <input key={j} type="number" value={v} placeholder="0" onChange={e => setValues(values.map((vv, jj) => jj === j ? e.target.value : vv))} style={{ width: 70, borderRadius: 8, padding: 5 }} />
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 120 }}>
            <div style={{ fontSize: 13, marginBottom: 2 }}>显示值平均值C̄<sub>a</sub></div>
            <div style={{ height: 32, display: 'flex', alignItems: 'center', background: '#fff', borderRadius: 8, padding: '0 10px', boxSizing: 'border-box', border: '1px solid #e0e0e0' }}>{avg}</div>
            <div style={{ fontSize: 13, margin: '8px 0 2px 0' }}>示值误差（%）</div>
            <div style={{ height: 32, display: 'flex', alignItems: 'center', background: '#fff', borderRadius: 8, padding: '0 10px', boxSizing: 'border-box', border: '1px solid #e0e0e0' }}>{error}</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 120 }}>
            <div style={{ fontSize: 13, marginBottom: 2 }}>误差是否合格</div>
            <div style={{ height: 32, display: 'flex', alignItems: 'center', background: '#fff', borderRadius: 8, padding: '0 10px', boxSizing: 'border-box', border: '1px solid #e0e0e0' }}>{errorPass}</div>
          </div>
        </div>
        {/* 系统响应时间 */}
        <div style={{ fontSize: 13, margin: '16px 0 8px 0' }}>系统响应时间（s）</div>
        {/* 第三行：T1/T2/T/平均值与合格性 */}
        <div style={{ display: 'flex', gap: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 60 }}>
            <div style={{ fontSize: 13, marginBottom: 2 }}>T1</div>
            {t1.map((v, j) => (
              <input key={j} type="number" value={v} placeholder="0" onChange={e => setT1(t1.map((vv, jj) => jj === j ? e.target.value : vv))} style={{ width: 50, borderRadius: 8, padding: 5 }} />
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 60 }}>
            <div style={{ fontSize: 13, marginBottom: 2 }}>T2</div>
            {t2.map((v, j) => (
              <input key={j} type="number" value={v} placeholder="0" onChange={e => setT2(t2.map((vv, jj) => jj === j ? e.target.value : vv))} style={{ width: 50, borderRadius: 8, padding: 5 }} />
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 60 }}>
            <div style={{ fontSize: 13, marginBottom: 2 }}>T1+T2</div>
            {tSumArr.map((v, j) => (
              <div key={j} style={{ height: 32, display: 'flex', alignItems: 'center', background: '#fff', borderRadius: 8, padding: '0 10px', boxSizing: 'border-box', border: '1px solid #e0e0e0', width: 50 }}>{v}</div>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 120 }}>
            <div style={{ fontSize: 13, marginBottom: 2 }}>平均值</div>
            <div style={{ height: 32, display: 'flex', alignItems: 'center', background: '#fff', borderRadius: 8, padding: '0 10px', boxSizing: 'border-box', border: '1px solid #e0e0e0' }}>{tAvg}</div>
            <div style={{ fontSize: 13, margin: '8px 0 2px 0' }}>平均值是否合格</div>
            <div style={{ height: 32, display: 'flex', alignItems: 'center', background: '#fff', borderRadius: 8, padding: '0 10px', boxSizing: 'border-box', border: '1px solid #e0e0e0' }}>{tPass}</div>
          </div>
        </div>
      </div>
      <FormulaDisplay title="公式与计算过程" defaultCollapsed={true}>
        {formulaType === "range" ? (
          <>
            <span><b>种类：{getKindLabel(kind)}，满量程小于等于{kind === "so2" ? "286" : kind === "nox" ? "410" : kind === "nmhc" ? "100" : rangeInfo.max}mg/m³</b></span><br />
            <span><b>示值误差计算公式为：</b></span><br />
            <span>L<sub>ei</sub> = (C̄<sub>a</sub> - C<sub>st</sub>) / F.S. × 100</span>
          </>
        ) : (
          <>
            <span><b>种类：{getKindLabel(kind)}，满量程大于{kind === "so2" ? "286" : kind === "nox" ? "410" : kind === "nmhc" ? "100" : rangeInfo.max}mg/m³</b></span><br />
            <span><b>示值误差计算公式为：</b></span><br />
            <span>L<sub>ei</sub> = (C̄<sub>a</sub> - C<sub>st</sub>) / C<sub>st</sub> × 100</span>
          </>
        )}<br />
        <b>计算过程：</b><br />
        {formulaStr}<br />
        {errorPassDetail && <span style={{ color: '#666' }}>{errorPassDetail}</span>}
      </FormulaDisplay>
      {/* 参考图片 */}
      <div style={{ textAlign: 'center', marginTop: 32 }}>
        <img
          src={calcImg}
          alt="技术要求与计算公式"
          style={{ maxWidth: 320, borderRadius: 12, cursor: 'pointer', boxShadow: '0 2px 8px #e5e9f2' }}
          onClick={() => setShowImg(true)}
        />
        {showImg && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowImg(false)}>
            <img src={calcImg} alt="技术要求与计算公式" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 16, background: '#fff' }} />
          </div>
        )}
        <div style={{ color: '#888', fontSize: 13, marginTop: 8 }}>点击图片可放大查看</div>
      </div>
    </div>
  );
};

export default IndicationError; 